import EmailIcon from '@material-ui/icons/Email'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ReactComponent as FlagSVG } from '@percent/admin-dashboard/common/assets/images/tableStatus/flag.svg'
import { getProperHref } from '@percent/admin-dashboard/common/utility/getProperHref'
import { getRegion } from '@percent/admin-dashboard/common/utility/getRegion'
import { useOrganisationsAccess } from '@percent/admin-dashboard/common/hooks'
import { OrganisationTypes } from '@percent/domain/verifications'
import { Alert, Badge, Button, ButtonText, CardContent, CardDashboard, FlagAvatar, Spacer } from '@percent/lemonade'
import styles from './AgentVerificationOrganisationDetails.module.scss'
import { OrganisationDetailsProps } from './AgentVerificationOrganisationDetails.types'
import { UpdateEmailModal } from './updateEmailModal/UpdateEmailModal'
import { UpdateWebsiteModal } from './updateWebsiteModal/UpdateWebsiteModal'

export function AgentVerificationOrganisationDetails({
  organisation,
  refresh,
  agentVerificationWebsite,
  agentVerification,
  agentVerificationLogs,
  autoApprovedSources
}: OrganisationDetailsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { canUpdateOrganisation } = useOrganisationsAccess()
  const [isModalOpened, setModalOpened] = useState(false)
  const [isWebsiteModalOpened, setIsWebsiteModalOpened] = useState(false)

  const associationEmailSent = !!agentVerificationLogs?.find(log => log.type === 'association_email_sent')

  const organisationTypes = Object.keys(organisation?.types ?? {}).filter(
    type => organisation?.types?.[type as OrganisationTypes] === 'yes'
  )

  return (
    <div className={styles.container}>
      <CardDashboard
        title={t('typography.organisationSectionTitle')}
        action={
          <Button
            size="small"
            variant="secondary"
            onClick={() => navigate(`/organizations/${organisation?.id}`, { state: 'agentVerification' })}
          >
            {t('button.goToOrganisationDetails')}
          </Button>
        }
      >
        {autoApprovedSources && autoApprovedSources.length > 0 && (
          <>
            <Alert variant="info" title={t('typography.domainsAutoApproved')}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {autoApprovedSources
                  .filter(source => source !== 'applicant_email')
                  .map(source => (
                    <li key={source}>{t(`typography.${source}`)}</li>
                  ))}
              </ul>
            </Alert>
            <Spacer size={4} />
          </>
        )}
        <CardContent title={t('typography.name')}>
          <span>{organisation?.name ?? t('typography.nullValue')}</span>
        </CardContent>
        {organisationTypes.length > 0 && (
          <CardContent title={t('typography.type')}>
            {organisationTypes.map(type => (
              <Fragment key={type}>
                <Badge variant="informative" icon="categories">
                  {t(`organisationTypes.${type as OrganisationTypes}`)}
                </Badge>
                <Spacer size={2} axis="horizontal" />
              </Fragment>
            ))}
          </CardContent>
        )}
        <CardContent title={t('typography.email')}>
          <div className={styles.emailFieldWrapper}>
            <span>
              {organisation?.email ?? (
                <div className={styles.noEmail}>
                  <FlagSVG />
                  {t('typography.emailNeeded')}
                </div>
              )}
            </span>
            {canUpdateOrganisation && (
              <ButtonText onClick={() => setModalOpened(true)} icon="edit">
                {t(`button.${organisation?.email ? 'editEmail' : 'addEmail'}`)}
              </ButtonText>
            )}
          </div>
          {associationEmailSent && (
            <div className={styles.greenBadge}>
              <EmailIcon /> {t('typography.emailSentToNonprofit')}
            </div>
          )}
        </CardContent>

        <CardContent title={t('typography.website')}>
          <div className={styles.organisationFieldWrapper}>
            <div>
              {organisation?.website && (
                <a
                  href={getProperHref(String(organisation?.website))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.websiteLink}
                >
                  {organisation?.website}
                </a>
              )}
              {!organisation?.website && agentVerificationWebsite && (
                <div className={styles.noWebsite}>
                  {t('typography.noWebsite')}
                  <br />
                  <br />
                  {t('typography.agentHasSuggested', {
                    website: agentVerificationWebsite
                  })}
                </div>
              )}
              {!organisation?.website && !agentVerificationWebsite && (
                <div className={styles.noEmail}>
                  <FlagSVG />
                  {t('typography.websiteNeeded')}
                </div>
              )}
            </div>
            <div className={styles.websiteActionsWrapper}>
              {canUpdateOrganisation && (
                <ButtonText onClick={() => setIsWebsiteModalOpened(true)} icon="edit">
                  {t(`button.${organisation?.website ? 'editWebsite' : 'addWebsite'}`)}
                </ButtonText>
              )}
            </div>
          </div>
        </CardContent>

        <CardContent title={t('typography.country')}>
          <span className={styles.countryFieldWrapper}>
            {organisation?.countryCode && <FlagAvatar code3={organisation?.countryCode} />}
            {organisation?.countryCode ? getRegion(organisation?.countryCode) : t('typography.nullValue')}
          </span>
        </CardContent>
        <CardContent title={t('typography.registryId')}>
          <span>{organisation?.registryId ?? t('typography.nullValue')}</span>
        </CardContent>
      </CardDashboard>

      {isModalOpened && (
        <UpdateEmailModal
          organisation={organisation}
          isModalOpened={isModalOpened}
          setModalOpened={setModalOpened}
          refresh={refresh}
          agentVerificationId={agentVerification?.id as string}
        />
      )}
      {isWebsiteModalOpened && (
        <UpdateWebsiteModal
          organisation={organisation}
          isModalOpened={isWebsiteModalOpened}
          setModalOpened={setIsWebsiteModalOpened}
          refresh={refresh}
          agentVerificationId={agentVerification?.id as string}
        />
      )}
    </div>
  )
}
